import axios from 'axios';
import store from '../store/store';
import { setUserInfo } from '../store/globalSlice';
import { stopNativeLiveLocation } from '../components/liveLocation/nativeBridge';

const LOGGED_OUT_USER_INFO = { username: '', token: '', roles: [] };

const logout = () => {
  stopNativeLiveLocation();
  localStorage.clear();
  store.dispatch(setUserInfo(LOGGED_OUT_USER_INFO));
  window.location.reload();
};

// Created once at module load and reused by every caller, so the
// isRefreshing/subscribers lock below actually guards concurrent 401s
// across the whole app instead of resetting on every component render.
const fetchData = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 60000,
});

fetchData.interceptors.request.use(
  async (config) => {
    try {
      const info = JSON.parse(localStorage.getItem('info')) || {};
      const token = info.token || null;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      console.error('Error reading token from localStorage:', e);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let subscribers = [];

const onRefreshed = (token) => {
  subscribers.forEach(({ resolve }) => resolve(token));
  subscribers = [];
};

const onRefreshFailed = (error) => {
  subscribers.forEach(({ reject }) => reject(error));
  subscribers = [];
};

const addSubscriber = (resolve, reject) => {
  subscribers.push({ resolve, reject });
};

fetchData.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error('Error received in response interceptor:', error);

    // If error.response is undefined, it's likely a network error
    if (!error.response) {
      console.error('Network or CORS error occurred:', error.message);
      alert('A network or server error occurred. Please try again.');
      return Promise.reject(error);
    }

    const { status } = error.response;
    const originalRequest = error.config;

    if (status === 401 && !originalRequest._retry) {
      const info = JSON.parse(localStorage.getItem('info')) || {};
      const refreshToken = info.refresh_token || null;

      if (!refreshToken) {
        // If no refresh token, clear storage and log out
        logout();
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        originalRequest._retry = true;

        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/auth/refresh-token`,
            { refresh_token: refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          );

          const newToken = response.data.token;
          const updatedInfo = { ...info, ...response.data };
          localStorage.setItem('info', JSON.stringify(updatedInfo));

          store.dispatch(setUserInfo(response.data));
          isRefreshing = false;
          onRefreshed(newToken);

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return fetchData(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          isRefreshing = false;
          onRefreshFailed(refreshError);
          logout();
          return Promise.reject(refreshError);
        }
      }

      // A refresh is already in flight: queue this request and replay it
      // (or reject it) once that refresh settles, instead of firing our own.
      return new Promise((resolve, reject) => {
        addSubscriber(
          (token) => {
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(fetchData(originalRequest));
          },
          (refreshError) => reject(refreshError)
        );
      });
    }

    // Retried request is still unauthorized (e.g. the refreshed token was
    // rejected too) - treat it as a real auth failure instead of leaving
    // the caller with a silently rejected request and no logged-out state.
    if (status === 401 && originalRequest._retry) {
      logout();
      return Promise.reject(error);
    }

    // Handle other errors with appropriate fallback
    console.error('Unhandled error:', error.response);
    return Promise.reject(error);
  }
);

const ApiClient = () => fetchData;

export default ApiClient;
